import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare, Zap, Wallet, Activity, TrendingUp, ChevronRight,
  Database, ArrowUpRight, Sparkles, Circle,
} from 'lucide-react';

import { portalDashboardApi, portalAnalyticsApi } from '../../services/api';
import { billingApi } from '../../services/billing';
import { usePortalStore } from '../../stores';
import { LiveNumber } from '../../components/instrument/LiveNumber';
import { Eyebrow, Display, LiveDot } from '../../components/instrument';
import { chartAxis, chartGrid, chartTooltip } from '../../lib/chartTheme';
import { formatMoney } from '../../lib/geo';
import ProjectPulse from '../../components/instrument/ProjectPulse';
import { MyAllocationCard } from '../../components/instrument/MyAllocationCard';

/** M4 — Instrument-Grade Portal Dashboard — credit-aware. */

const PortalDashboard = () => {
  const { tenant, user } = usePortalStore();
  const navigate = useNavigate();

  const { data: stats = {} } = useQuery({
    queryKey: ['portalStats', user?.id],
    queryFn: () => portalDashboardApi.getStats().then((r) => r.data),
    staleTime: 30000,
    enabled: !!user?.id,
  });
  const { data: timeseries = [] } = useQuery({
    queryKey: ['portalTimeseries', '7d', user?.id],
    queryFn: () => portalAnalyticsApi.getTimeseries('7d').then((r) => r.data),
    staleTime: 30000,
    enabled: !!user?.id,
  });
  const { data: wallet } = useQuery({
    queryKey: ['portalWallet', user?.id],
    queryFn: () => billingApi.wallet().then((r) => r.data),
    refetchInterval: 30000,
    enabled: !!user?.id,
  });

  // Belt-and-braces: the persisted react-query cache can get sticky across
  // sessions; this forced fetch ensures the dashboard always reflects the
  // latest server-side wallet on mount.
  const [walletDirect, setWalletDirect] = useState(null);
  useEffect(() => {
    let alive = true;
    billingApi.wallet().then((r) => alive && setWalletDirect(r.data)).catch(() => {});
    const onCreditsChanged = () => billingApi.wallet().then((r) => alive && setWalletDirect(r.data)).catch(() => {});
    window.addEventListener('credits:changed', onCreditsChanged);
    return () => { alive = false; window.removeEventListener('credits:changed', onCreditsChanged); };
  }, [user?.id]);
  const liveWallet = walletDirect || wallet;

  const recentConvos = stats.recent_conversations || [];
  const knowledgeBases = stats.knowledge_bases || [];
  const tokensUsed = stats.total_tokens || 0;
  const baseCredits = Math.ceil(tokensUsed / 100) / 10;
  const credit_balance = (liveWallet?.effective_balance ?? liveWallet?.credit_balance) || 0;
  const currency = liveWallet?.currency || 'INR';
  const ratePerCredit = currency === 'INR' ? 9.5 : 0.58;
  const approxSpend = baseCredits * ratePerCredit;

  // Build the 4 stat tiles dynamically from credit-aware data
  const tiles = [
    {
      key: 'wallet', label: 'Credit balance', icon: Wallet, accent: '#10B981',
      render: () => (
        <span>
          <LiveNumber value={credit_balance} decimals={1} /> <span className="text-[18px] text-nexus-text-tertiary">cr</span>
        </span>
      ),
    },
    {
      key: 'tokens_used', label: 'Tokens used', icon: Zap, accent: '#F26B1F',
      render: () => (
        tokensUsed >= 1000
          ? <span className="ig-numeric">{(tokensUsed / 1000).toFixed(1)}<span className="text-[18px] text-nexus-text-tertiary">K</span></span>
          : <LiveNumber value={tokensUsed} />
      ),
      sub: `≈ ${baseCredits.toFixed(1)} base credits`,
    },
    {
      key: 'conversations', label: 'Conversations', icon: MessageSquare, accent: '#2E70AD',
      render: () => <LiveNumber value={stats.total_conversations || 0} />,
    },
    {
      key: 'spend', label: 'Approx. spend', icon: Activity, accent: '#92580A',
      render: () => <span>{formatMoney(approxSpend, currency)}</span>,
      sub: 'At base bracket rate',
    },
  ];

  return (
    <div className="space-y-6" data-testid="portal-dashboard">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <Eyebrow>Mission control · {tenant?.account_type === 'personal' ? 'Personal' : 'Credits'}</Eyebrow>
          <Display as="h1" size="md" className="mt-2 text-nexus-text-primary">
            Welcome back, <span style={{ color: '#F26B1F' }}>{(user?.name || user?.email || '').split(' ')[0]}</span>.
          </Display>
          <p className="text-[13.5px] text-nexus-text-secondary mt-1.5 flex items-center gap-2">
            <LiveDot variant="success" />
            <span className="ig-mono text-[11px] text-nexus-text-tertiary">Cognara Engine online · Ultra 4.7</span>
          </p>
        </div>
        <button
          onClick={() => navigate('/portal/chat')}
          className="ig-cta h-10 px-5 rounded-xl text-[13px] font-medium inline-flex items-center gap-2"
          style={{ background: '#1E4E8C', color: '#E6EEF9', boxShadow: 'var(--tinted-shadow-cta)' }}
          data-testid="dashboard-new-chat"
        >
          <Sparkles size={14} /> New conversation
        </button>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {tiles.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div
              key={s.key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05, duration: 0.36, ease: [0.16, 1, 0.3, 1] }}
              className="ig-elevated p-4 relative overflow-hidden group"
              data-testid={`stat-${s.key}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                     style={{ background: `${s.accent}15`, border: `1px solid ${s.accent}30` }}>
                  <Icon size={15} style={{ color: s.accent }} />
                </div>
                <ArrowUpRight size={12} className="text-nexus-text-tertiary opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <Eyebrow>{s.label}</Eyebrow>
              <div className="ig-display text-[30px] mt-1 text-nexus-text-primary leading-[1.1]" style={{ letterSpacing: '-0.02em' }}>
                {s.render()}
              </div>
              {s.sub && <div className="text-[11px] text-nexus-text-tertiary mt-1">{s.sub}</div>}
            </motion.div>
          );
        })}
      </div>

      {/* My Allocation — member-facing personal credit panel */}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-4">
          <MyAllocationCard />
        </div>
      </div>

      {/* Bento — Usage chart + Project Pulse */}
      <div className="grid grid-cols-12 gap-4">
        {/* Usage area chart — 8 cols */}
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.4 }}
          className="col-span-12 lg:col-span-8 ig-elevated p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <Eyebrow>Telemetry · 7-day window</Eyebrow>
              <h3 className="ig-display text-[18px] text-nexus-text-primary mt-1 flex items-center gap-2">
                <TrendingUp size={14} className="text-nexus-accent-blue" /> Tokens consumed
              </h3>
            </div>
            <button onClick={() => navigate('/portal/analytics')}
                    className="text-[12px] text-nexus-text-tertiary hover:text-nexus-accent-mint transition-colors flex items-center gap-1">
              Full analytics <ChevronRight size={11} />
            </button>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={timeseries} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
              <defs>
                <linearGradient id="dashTokenGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#1E4E8C" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#1E4E8C" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...chartGrid} />
              <XAxis dataKey="date" tick={chartAxis.tick} axisLine={false} tickLine={false} />
              <YAxis tick={chartAxis.tick} axisLine={false} tickLine={false} width={50} />
              <Tooltip {...chartTooltip} />
              <Area dataKey="tokens" name="Tokens" stroke="#2E70AD" fill="url(#dashTokenGrad)" strokeWidth={2} animationDuration={1200} />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Project Pulse wow moment — 4 cols */}
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.4 }}
          className="col-span-12 lg:col-span-4 ig-elevated p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <Eyebrow>Project pulse</Eyebrow>
              <h3 className="ig-display text-[18px] text-nexus-text-primary mt-1">Active orbits</h3>
            </div>
            <span className="ig-mono text-[10px] text-nexus-text-tertiary flex items-center gap-1"><Circle size={6} fill="currentColor" /> live</span>
          </div>
          <ProjectPulse convos={recentConvos} />
        </motion.div>
      </div>

      {/* Bento row 2 — Knowledge + Recent convos */}
      <div className="grid grid-cols-12 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 0.4 }}
          className="col-span-12 lg:col-span-5 ig-elevated p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="ig-display text-[16px] text-nexus-text-primary flex items-center gap-2">
              <Database size={14} className="text-nexus-accent-blue" /> Knowledge
            </h3>
            <button onClick={() => navigate('/portal/knowledge')}
                    className="text-[12px] text-nexus-text-tertiary hover:text-nexus-accent-mint transition-colors flex items-center gap-1">
              Manage <ChevronRight size={11} />
            </button>
          </div>
          {knowledgeBases.length > 0 ? (
            <div className="space-y-1.5">
              {knowledgeBases.slice(0, 6).map((kb, i) => (
                <motion.div
                  key={kb.id || i}
                  initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + i * 0.04 }}
                  className="flex items-center justify-between px-3 py-2 rounded-lg transition-colors hover:bg-white/[0.04]"
                >
                  <span className="text-[13px] text-nexus-text-primary truncate">{kb.name}</span>
                  <span className="ig-mono text-[10px] text-nexus-text-tertiary">{kb.chunk_count || 0} chunks</span>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-[12px] text-nexus-text-tertiary">No knowledge bases yet</p>
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.4 }}
          className="col-span-12 lg:col-span-7 ig-elevated p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="ig-display text-[16px] text-nexus-text-primary flex items-center gap-2">
              <MessageSquare size={14} className="text-nexus-accent-blue" /> Recent conversations
            </h3>
            <button onClick={() => navigate('/portal/chat')}
                    className="text-[12px] text-nexus-text-tertiary hover:text-nexus-accent-mint transition-colors flex items-center gap-1">
              Open chat <ChevronRight size={11} />
            </button>
          </div>
          {recentConvos.length > 0 ? (
            <div className="space-y-1.5">
              {recentConvos.slice(0, 6).map((conv, i) => (
                <motion.button
                  key={conv.id || i}
                  initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 + i * 0.04 }}
                  onClick={() => navigate(`/portal/chat/${conv.id}`)}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg hover:bg-white/[0.04] transition-colors group text-left"
                  data-testid={`recent-conv-${i}`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <MessageSquare size={12} className="text-nexus-text-tertiary group-hover:text-nexus-accent-mint transition-colors flex-shrink-0" />
                    <span className="text-[13px] text-nexus-text-primary truncate">{conv.title}</span>
                  </div>
                  <span className="ig-mono text-[10px] text-nexus-text-tertiary flex-shrink-0">
                    {conv.updated_at ? new Date(conv.updated_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—'}
                  </span>
                </motion.button>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-[12px] text-nexus-text-tertiary">Start a conversation to see history</p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default PortalDashboard;
